<script setup>
// Slide-over help drawer — shows a help article (chrome-less embed) in an iframe
// so users read guides without leaving the dashboard.
import { computed } from 'vue';
import { useMesselloHelp } from 'dashboard/composables/useMesselloHelp';

const { drawerOpen, drawerTitle, embedUrl, fullUrl, closeHelp } =
  useMesselloHelp();

const src = computed(() => (drawerOpen.value ? embedUrl() : ''));
</script>

<template>
  <div v-if="drawerOpen" class="mz-help-drawer">
    <div class="mz-help-scrim" @click="closeHelp" />
    <aside class="mz-help-panel" role="dialog" aria-label="Help">
      <header class="mz-help-head">
        <span class="mz-help-title">{{ drawerTitle || 'Help' }}</span>
        <div class="mz-help-actions">
          <a
            :href="fullUrl()"
            target="_blank"
            rel="noopener"
            class="mz-help-open"
            title="Open full article"
          >Open ↗</a>
          <button class="mz-help-close" aria-label="Close" @click="closeHelp">
            ✕
          </button>
        </div>
      </header>
      <iframe
        v-if="src"
        :src="src"
        class="mz-help-frame"
        title="Help article"
      />
    </aside>
  </div>
</template>

<style scoped>
.mz-help-drawer {
  position: fixed;
  inset: 0;
  z-index: 10000;
}
.mz-help-scrim {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
}
.mz-help-panel {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 460px;
  max-width: 92vw;
  background: #fff;
  box-shadow: -12px 0 40px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  animation: mz-slide-in 0.22s ease;
}
@keyframes mz-slide-in {
  from {
    transform: translateX(30px);
    opacity: 0.6;
  }
  to {
    transform: none;
    opacity: 1;
  }
}
.mz-help-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e6eaf1;
  flex-shrink: 0;
}
.mz-help-title {
  font-weight: 700;
  font-size: 15px;
  color: #0f172a;
}
.mz-help-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mz-help-open {
  font-size: 13px;
  font-weight: 600;
  color: #2a55d6;
  text-decoration: none;
}
.mz-help-close {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #64748b;
  font-size: 15px;
  padding: 4px 6px;
  border-radius: 6px;
}
.mz-help-close:hover {
  background: #f2f6ff;
  color: #0f172a;
}
.mz-help-frame {
  border: 0;
  width: 100%;
  flex: 1;
}
</style>
